export interface VoucherBookingItem {
    product_id: number;
    quantity: number;
}

export interface StoredVoucherBooking {
    codes: string[];
    items: VoucherBookingItem[];
    bookingIds: number[];
    reservedCodes: string[];
    expiresAt?: string;
    createdAt: string;
}

export const CHECKOUT_VOUCHER_BOOKING_STORAGE_KEY = "checkout_voucher_booking";

function normalizeCodes(codes: string[]): string[] {
    return [...codes].filter(Boolean).sort();
}

function normalizeItems(items: VoucherBookingItem[]): VoucherBookingItem[] {
    return [...items]
        .map((item) => ({
            product_id: Number(item.product_id),
            quantity: Number(item.quantity),
        }))
        .sort((left, right) => left.product_id - right.product_id || left.quantity - right.quantity);
}

function arraysEqual<T>(left: T[], right: T[]): boolean {
    if (left.length !== right.length) {
        return false;
    }

    return left.every((value, index) => JSON.stringify(value) === JSON.stringify(right[index]));
}

function isExpired(expiresAt?: string): boolean {
    if (!expiresAt) {
        return false;
    }

    const expiresAtTime = Date.parse(expiresAt);
    if (Number.isNaN(expiresAtTime)) {
        return false;
    }

    return expiresAtTime <= Date.now();
}

export function saveVoucherBooking(payload: Omit<StoredVoucherBooking, "createdAt">): void {
    const nextValue: StoredVoucherBooking = {
        ...payload,
        createdAt: new Date().toISOString(),
    };

    localStorage.setItem(CHECKOUT_VOUCHER_BOOKING_STORAGE_KEY, JSON.stringify(nextValue));
}

export function readVoucherBooking(): StoredVoucherBooking | null {
    try {
        const raw = localStorage.getItem(CHECKOUT_VOUCHER_BOOKING_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as Partial<StoredVoucherBooking>;
        if (!Array.isArray(parsed.codes) || !Array.isArray(parsed.items) || !Array.isArray(parsed.bookingIds) || !Array.isArray(parsed.reservedCodes)) {
            return null;
        }

        if (typeof parsed.expiresAt === "string" && isExpired(parsed.expiresAt)) {
            clearVoucherBooking();
            return null;
        }

        return {
            codes: parsed.codes.map(String),
            items: parsed.items.map((item) => ({
                product_id: Number(item.product_id),
                quantity: Number(item.quantity),
            })),
            bookingIds: parsed.bookingIds.map(Number).filter(Number.isFinite),
            reservedCodes: parsed.reservedCodes.map(String),
            expiresAt: typeof parsed.expiresAt === "string" ? parsed.expiresAt : undefined,
            createdAt: typeof parsed.createdAt === "string" ? parsed.createdAt : new Date().toISOString(),
        };
    } catch (error) {
        console.error("Failed to read voucher booking storage", error);
        return null;
    }
}

export function clearVoucherBooking(): void {
    localStorage.removeItem(CHECKOUT_VOUCHER_BOOKING_STORAGE_KEY);
}

export function matchesVoucherBooking(codes: string[], items: VoucherBookingItem[], stored: StoredVoucherBooking | null): boolean {
    if (!stored || isExpired(stored.expiresAt)) {
        return false;
    }

    return arraysEqual(normalizeCodes(codes), normalizeCodes(stored.codes))
        && arraysEqual(normalizeItems(items), normalizeItems(stored.items));
}